import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { CatalogGeoFacetsSchema } from './geo.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Preferred-hotel master (B4.7).
 *
 * The DB-side hotel catalog distinct from `TboHotel` (the TBO mirror). The
 * B5 itinerary HOTEL picker queries both and deduplicates by
 * `tboHotelCode`. Legacy heritage: the real preferred-hotel catalog lived
 * in `tbl_Activities WHERE Type='Hotel'` denormalised into ~199 rows; the
 * B4.7 ETL groups those into proper Hotel + HotelRoom children.
 *
 * BigInt paise columns serialise to numeric strings on the wire (Swagger
 * + JSON-Schema friendly).
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const HotelStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type HotelStatus = z.infer<typeof HotelStatusSchema>;

// ---------------------------------------------------------------------------
// HotelPhoto + amenity JSON shapes (validated as `unknown[]` on the wire to
// keep the JSON column tolerant of legacy variants; the editor enforces the
// shape client-side via the typed helpers below).
// ---------------------------------------------------------------------------

export const HotelPhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url('Photo URL must be a valid URL').optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), {
    message: 'Photo needs a mediaId or a url',
  });
export type HotelPhoto = z.infer<typeof HotelPhotoSchema>;

export const HotelPhotoListSchema = z.array(HotelPhotoSchema);
export const HotelAmenityListSchema = z.array(z.string().trim().min(1).max(80));

// ---------------------------------------------------------------------------
// HotelRoom (child of Hotel)
// ---------------------------------------------------------------------------

// Meal options + extra-bed pricing live on the price period (HotelInventory),
// not the room - they can vary by season (B5.16). The room is identity +
// occupancy only.

export const HotelRoomOutSchema = z.object({
  id: UuidSchema,
  hotelId: UuidSchema,
  legacyActivityId: z.number().int().nullable(),
  name: z.string(),
  roomTypeId: UuidSchema.nullable(),
  roomTypeName: z.string().nullable(),
  occupancyTypeId: UuidSchema.nullable(),
  occupancyTypeName: z.string().nullable(),
  mealPlanId: UuidSchema.nullable(),
  mealPlanCode: z.string().nullable(),
  description: z.string().nullable(),
  photos: z.unknown().nullable(),
  minOccupancy: z.number().int(),
  /** Max bed-needing occupants. `max == base` = strict (no extra bed); `max >
   *  base` allows `max - base` extra beds. Base occupancy lives on the rate card. */
  maxOccupancy: z.number().int(),
  roomInventoryCount: z.number().int(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type HotelRoomOut = z.infer<typeof HotelRoomOutSchema>;

const HotelRoomCoreFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Room name is required').max(200),
  roomTypeId: UuidSchema.nullable().optional(),
  occupancyTypeId: UuidSchema.nullable().optional(),
  mealPlanId: UuidSchema.nullable().optional(),
  description: z.string().max(5000).nullable().optional(),
  photos: HotelPhotoListSchema.nullable().optional(),
  minOccupancy: z.number().int().min(1).max(20).optional(),
  maxOccupancy: z.number().int().min(1).max(20).optional(),
  roomInventoryCount: z.number().int().min(0).max(10_000).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateHotelRoomRequestSchema = HotelRoomCoreFieldsSchema;
export type CreateHotelRoomRequest = z.infer<typeof CreateHotelRoomRequestSchema>;

export const UpdateHotelRoomRequestSchema = HotelRoomCoreFieldsSchema.partial();
export type UpdateHotelRoomRequest = z.infer<typeof UpdateHotelRoomRequestSchema>;

// ---------------------------------------------------------------------------
// Hotel (master)
// ---------------------------------------------------------------------------

export const HotelListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyActivityName: z.string().nullable(),
  name: z.string(),
  slug: z.string().nullable(),
  starRating: z.number().int(),
  hotelChain: z.string().nullable(),
  countryId: UuidSchema.nullable(),
  countryName: z.string().nullable(),
  cityId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  status: HotelStatusSchema,
  isApprovedByAdmin: z.boolean(),
  tboHotelCode: z.string().nullable(),
  roomCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type HotelListItem = z.infer<typeof HotelListItemSchema>;

export const HotelListQuerySchema = PaginationQuerySchema.extend({
  status: HotelStatusSchema.optional(),
  cityId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  starRating: z.coerce.number().int().min(1).max(5).optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  /** When true, includes hotels backed by a TBO mirror (FAQ #8 dedup-side). */
  hasTboMirror: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type HotelListQuery = z.infer<typeof HotelListQuerySchema>;

export const HotelListResponseSchema = paginatedResponseSchema(HotelListItemSchema);
export type HotelListResponse = z.infer<typeof HotelListResponseSchema>;

/** In-use countries / cities for the Hotels list filters - see
 *  {@link CatalogGeoFacetsSchema}. */
export const HotelFacetsSchema = CatalogGeoFacetsSchema;
export type HotelFacets = z.infer<typeof HotelFacetsSchema>;

export const HotelDetailSchema = HotelListItemSchema.extend({
  brand: z.string().nullable(),
  address: z.string().nullable(),
  neighbourhood: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  amenities: z.unknown().nullable(),
  photos: z.unknown().nullable(),
  checkInTime: z.string().nullable(),
  checkOutTime: z.string().nullable(),
  policiesHtml: z.string().nullable(),
  contractRef: z.string().nullable(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable(),
  markupValue: z.number(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  lastApprovedById: UuidSchema.nullable(),
  rooms: z.array(HotelRoomOutSchema),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type HotelDetail = z.infer<typeof HotelDetailSchema>;

const HotelCoreFieldsSchema = z.object({
  // Identity
  name: z.string().trim().min(1, 'Hotel name is required').max(300),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  starRating: z.number().int().min(0).max(5).optional(),
  hotelChain: z.string().trim().max(120).nullable().optional(),
  brand: z.string().trim().max(120).nullable().optional(),
  // Location
  countryId: UuidSchema.nullable().optional(),
  cityId: UuidSchema.nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  neighbourhood: z.string().trim().max(160).nullable().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  amenities: HotelAmenityListSchema.nullable().optional(),
  photos: HotelPhotoListSchema.nullable().optional(),
  checkInTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm format')
    .nullable()
    .optional(),
  checkOutTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm format')
    .nullable()
    .optional(),
  policiesHtml: z.string().max(20_000).nullable().optional(),
  // Supplier / commercial
  vendorSupplierId: UuidSchema.nullable().optional(),
  contractRef: z.string().trim().max(120).nullable().optional(),
  markupKind: z.enum(['PERCENT', 'FLAT']).nullable().optional(),
  markupValue: z.number().nonnegative().optional(),
  // TBO link
  tboHotelCode: z.string().trim().min(1).max(64).nullable().optional(),
  // Approval workflow
  status: HotelStatusSchema.optional(),
});

export const CreateHotelRequestSchema = HotelCoreFieldsSchema;
export type CreateHotelRequest = z.infer<typeof CreateHotelRequestSchema>;

export const UpdateHotelRequestSchema = HotelCoreFieldsSchema.partial();
export type UpdateHotelRequest = z.infer<typeof UpdateHotelRequestSchema>;

/** Approval action (admin signs off). Mirrors the master approval pattern. */
export const HotelApprovalRequestSchema = z.object({
  approve: z.boolean(),
});
export type HotelApprovalRequest = z.infer<typeof HotelApprovalRequestSchema>;
